import { getAccessToken, logout } from './auth';

export type RepairStatus = 0 | 1 | 2; // 0=nicht repariert, 1=repariert, 2=Totalschaden

export interface RepReport {
  id:                 number;
  repair_report:      string;
  repair_status:      RepairStatus | null;
  is_intern_repaired: boolean;
  is_goodwill:        boolean;
  is_swap:            boolean;
  swap_imei_sn:       string | null;
  brutto:             number | null;
  netto:              number | null;
  costs:              number | null;
  repair_fee_id:      number | null;
  repair_fee_costs:   number | null;
  creator_name:       string;
  creation_timestamp: string;
}

/** Textbaustein-Art: 1 = Befund/Fehler, 2 = durchgeführte Arbeit. */
export const TEMPLATE_TYPE_ERROR = 1;
export const TEMPLATE_TYPE_WORK  = 2;

/** Technischer Schlüssel des Bausteins, der einen Austausch kennzeichnet. */
export const TEMPLATE_KEY_SWAP = 'SWAP';

export interface RepReportTemplate {
  id:               number;
  template_key:     string;
  description:      string;
  text:             string;
  template_type_id: number;
}

export interface RepairFee {
  id:          number;
  description: string;
  value:       number;
}

export interface RepReportCreatePayload {
  repair_status:       RepairStatus;
  repair_report:       string;
  is_intern_repaired?: boolean;
  is_goodwill?:        boolean;
  /** Gewählter Arbeits-Textbaustein. Ist es SWAP, gilt der Bericht als Austausch. */
  work_template_id?:   number | null;
  swap_imei_sn?:       string;
  brutto?:             number | null;
  costs?:              number | null;
  repair_fee_id?:      number | null;
}

interface ApiEnvelope<T> {
  result: 'SUCCESS' | 'UNAUTHORIZED' | 'FORBIDDEN' | 'NOT_FOUND' | 'BAD_REQUEST' | 'INTERNAL_ERROR' | 'METHOD_NOT_ALLOWED' | 'CONFLICT';
  reason?: string;
  fields?: string[];
  items?: T[];
  item?:  T;
}

export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
    readonly code: ApiEnvelope<unknown>['result'] | 'network' | 'parse',
    readonly fields?: string[],
  ) {
    super(message);
  }
}

const BASE = `${window.location.origin}/api`;

async function request<T>(path: string, init: RequestInit = {}): Promise<ApiEnvelope<T>> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(path, {
      ...init,
      headers: {
        'Accept':        'application/json',
        'Content-Type':  'application/json',
        'Authorization': `Bearer ${token}`,
        ...(init.headers ?? {}),
      },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }

  let body: ApiEnvelope<T>;
  try {
    body = (await resp.json()) as ApiEnvelope<T>;
  } catch {
    throw new ApiError(`Unlesbare Server-Antwort (${resp.status})`, resp.status, 'parse');
  }

  if (!resp.ok || body.result !== 'SUCCESS') {
    throw new ApiError(body.reason ?? `Server-Fehler ${resp.status}`, resp.status, body.result, body.fields);
  }

  return body;
}

export async function listRepReports(ticketId: number): Promise<RepReport[]> {
  const body = await request<RepReport>(`${BASE}/tickets/${ticketId}/repair-reports`, { method: 'GET' });
  return body.items ?? [];
}

export async function createRepReport(ticketId: number, payload: RepReportCreatePayload): Promise<RepReport> {
  const body = await request<RepReport>(`${BASE}/tickets/${ticketId}/repair-reports`, {
    method: 'POST',
    body:   JSON.stringify(payload),
  });
  if (!body.item) {
    throw new ApiError('Server hat kein item zurückgegeben.', 500, 'INTERNAL_ERROR');
  }
  return body.item;
}

export async function deleteRepReport(ticketId: number, reportId: number): Promise<void> {
  await request(`${BASE}/tickets/${ticketId}/repair-reports/${reportId}`, { method: 'DELETE' });
}

/**
 * Aktive Textbausteine für den Reparaturbericht. Ohne Filter kommen beide Arten,
 * damit beide Auswahllisten mit einem Aufruf gefüllt werden können.
 */
export async function listRepReportTemplates(templateTypeId?: number): Promise<RepReportTemplate[]> {
  const query = templateTypeId !== undefined ? `?template_type_id=${templateTypeId}` : '';
  const body = await request<RepReportTemplate>(`${BASE}/rep-report-templates${query}`, { method: 'GET' });
  return body.items ?? [];
}

export async function listRepairFees(): Promise<RepairFee[]> {
  const body = await request<RepairFee>(`${BASE}/repair-fees`, { method: 'GET' });
  return body.items ?? [];
}

export async function fetchRepReportPdfBlob(ticketId: number, reportId: number): Promise<Blob> {
  const token = await getAccessToken();
  if (!token) {
    throw new ApiError('Nicht angemeldet.', 401, 'UNAUTHORIZED');
  }

  let resp: Response;
  try {
    resp = await fetch(`${BASE}/tickets/${ticketId}/repair-reports/${reportId}/pdf`, {
      method:  'GET',
      headers: { 'Authorization': `Bearer ${token}` },
    });
  } catch (e) {
    throw new ApiError(`Netzwerkfehler: ${(e as Error).message}`, 0, 'network');
  }

  if (resp.status === 401) {
    logout('none');
  }
  if (!resp.ok) {
    throw new ApiError(`PDF nicht abrufbar (${resp.status})`, resp.status, 'NOT_FOUND');
  }
  return await resp.blob();
}
